const STORAGE_KEY = "vbs.companyProfiles";

export function normalizeCompanyKey(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(key) ? key : null;
}

function normalizeCountry(value) {
  const country = String(value || "")
    .trim()
    .toUpperCase();
  if (country === "MEX" || country === "MEXICO") return "MX";
  if (country === "USA" || country === "U.S." || country === "UNITED STATES")
    return "US";
  return /^[A-Z]{2}$/.test(country) ? country : "";
}

function normalizeCurrency(value, country) {
  const currency = String(value || "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{3}$/.test(currency)) return currency;
  return country === "MX" ? "MXN" : "USD";
}

function normalizeLocale(value, country) {
  const locale = String(value || "").trim();
  if (/^[a-z]{2}-[A-Z]{2}$/.test(locale)) return locale;
  return country === "MX" ? "es-MX" : "en-US";
}

export function normalizeCompanyProfile(row) {
  const key = normalizeCompanyKey(row?.key);
  if (!key) return null;
  const erpMode =
    String(row?.erpMode || "").trim().toLowerCase() === "resale"
      ? "resale"
      : "manufacturing";
  const accountingCountry = normalizeCountry(
    row?.accountingCountry || row?.country
  );
  const baseCurrency = normalizeCurrency(row?.baseCurrency, accountingCountry);
  const defaultLocale = normalizeLocale(row?.defaultLocale, accountingCountry);
  return {
    key,
    name: String(row?.name || "").trim() || key.toUpperCase(),
    erpMode,
    accountingCountry,
    baseCurrency,
    defaultLocale,
    supportedLocales: Array.isArray(row?.supportedLocales)
      ? row.supportedLocales.filter(Boolean)
      : ["en-US", "es-MX"],
    stripePublishableKey: String(row?.stripePublishableKey || "").trim(),
    isMexicoAccountingCompany:
      accountingCountry === "MX" || baseCurrency === "MXN",
    isUnitedStatesAccountingCompany:
      accountingCountry === "US" || baseCurrency === "USD",
  };
}

export function cacheCompanyProfiles(rows) {
  if (typeof window === "undefined") return;
  const normalized = (Array.isArray(rows) ? rows : [])
    .map(normalizeCompanyProfile)
    .filter(Boolean);
  if (!normalized.length) return;
  try {
    const existing = readCompanyProfiles();
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...existing,
        ...Object.fromEntries(normalized.map((row) => [row.key, row])),
      })
    );
  } catch {
    // Best effort cache only.
  }
}

export function readCompanyProfiles() {
  if (typeof window === "undefined") return {};
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    if (!raw || typeof raw !== "object") return {};
    const rows = Object.values(raw)
      .map(normalizeCompanyProfile)
      .filter(Boolean);
    return Object.fromEntries(rows.map((row) => [row.key, row]));
  } catch {
    return {};
  }
}

export function readCompanyProfile(companyKey) {
  const key = normalizeCompanyKey(companyKey);
  if (!key) return null;
  return readCompanyProfiles()[key] || null;
}

export function currencyForCompany(companyKey, fallback = "USD") {
  const profile = readCompanyProfile(companyKey);
  if (profile?.baseCurrency) return profile.baseCurrency;
  return fallback;
}

export function localeForCompany(companyKey, fallback = "en-US") {
  const profile = readCompanyProfile(companyKey);
  if (profile?.defaultLocale) return profile.defaultLocale;
  const key = normalizeCompanyKey(companyKey);
  if (key === "mx") return "es-MX";
  return fallback;
}

export function countryForCompany(companyKey, fallback = "US") {
  const profile = readCompanyProfile(companyKey);
  if (profile?.accountingCountry) return profile.accountingCountry;
  return fallback;
}

export function stripePublishableKeyForCompany(companyKey, fallback = "") {
  const profile = readCompanyProfile(companyKey);
  const key = normalizeCompanyKey(companyKey);
  const tenantSuffix = key ? key.toUpperCase().replace(/[^A-Z0-9]/g, "_") : "";
  const tenantEnvKey = tenantSuffix
    ? process.env[`REACT_APP_STRIPE_PUBLISHABLE_KEY_${tenantSuffix}`]
    : "";
  const globalEnvKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  return String(
    profile?.stripePublishableKey ||
      tenantEnvKey ||
      globalEnvKey ||
      fallback ||
      ""
  ).trim();
}
